import React from "react";
import { Avatar, Button } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import type { StoreApprovalItem } from "../types";
import martEmptyImg from "assets/images/mart-empty.png";

export type StoreApprovalCardProps = {
  item: StoreApprovalItem;
  onApprove: (item: StoreApprovalItem) => void;
  onDecline: (item: StoreApprovalItem) => void;
  onReview?: (item: StoreApprovalItem) => void;
  approveLoading?: boolean;
  declineLoading?: boolean;
  actionsDisabled?: boolean;
};

export const StoreApprovalCard: React.FC<StoreApprovalCardProps> = ({
  item,
  onApprove,
  onDecline,
  onReview,
  approveLoading = false,
  declineLoading = false,
  actionsDisabled = false,
}) => {
  const initials = item.ownerName
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

  const isApproved = item.status === "approved";

  return (
    <div className="store-approval-item-card">
      <div
        className="store-approval-item-logo-wrap"
        role={onReview ? "button" : undefined}
        onClick={onReview ? () => onReview(item) : undefined}
      >
        <div
          className="store-approval-item-logo"
          style={{ backgroundColor: item.logoColor || "#E3F2FD" }}
        >
          <img
            src={item.logoUrl || martEmptyImg}
            alt={item.storeName}
            className="store-approval-item-logo-img"
            onError={(event) => {
              if (!event.currentTarget.src.endsWith(martEmptyImg)) {
                event.currentTarget.src = martEmptyImg;
              }
            }}
          />
        </div>
        {isApproved && (
          <span className="store-approval-item-logo-badge" aria-hidden>
            <CheckCircleFilled />
          </span>
        )}
      </div>

      <div
        className="store-approval-item-mid"
        role={onReview ? "button" : undefined}
        onClick={onReview ? () => onReview(item) : undefined}
      >
        <div className="store-approval-item-name">{item.storeName}</div>
        <div className="store-approval-item-owner">
          <Avatar
            size={18}
            style={{
              background: item.ownerAvatarColor || "#E8F5E9",
              color: "#1a1a1a",
              fontSize: 10,
            }}
          >
            {initials}
          </Avatar>
          <span className="store-approval-item-owner-name">{item.ownerName}</span>
        </div>
      </div>

      <div className="store-approval-item-actions">
        {onReview && (
          <Button
            size="small"
            className="store-approval-btn store-approval-btn-review"
            onClick={(e) => {
              e.stopPropagation();
              onReview(item);
            }}
          >
            Review
          </Button>
        )}
        {!isApproved && (
          <Button
            size="small"
            className="store-approval-btn store-approval-btn-approve"
            onClick={(e) => {
              e.stopPropagation();
              onApprove(item);
            }}
            loading={approveLoading}
            disabled={actionsDisabled}
          >
            Approve
          </Button>
        )}
        {!isApproved && (
          <Button
            size="small"
            className="store-approval-btn store-approval-btn-decline"
            onClick={(e) => {
              e.stopPropagation();
              onDecline(item);
            }}
            loading={declineLoading}
            disabled={actionsDisabled}
          >
            Decline
          </Button>
        )}
      </div>
    </div>
  );
};

export default StoreApprovalCard;
